<template>
    <div class="collection-thumbnail" />
    <div class="collection-thumbnail__overlay">
        <div class="collection-thumbnail__info">
            <div class="collection-thumbnail__title">{{ t('resources.collection') }}</div>
            <div>
                <span class="collection-thumbnail__label">{{ collectionEntries.reduce((sum, {amount}) => sum + amount, 0) }}</span>
                <span class="collection-thumbnail__label">
                    {{ t('resources.resourcesCenter').toLowerCase() }}
                </span>
            </div>
        </div>
    </div>
</template>
<script lang="ts" setup>
    import {computed} from 'vue';
    import type {FilledContentRelationshipField} from '@prismicio/types';
    import type {CollectionDocument, CollectionDocumentData} from '@prismicio-types';
    import {createContentHelpers, useTranslation} from '#imports';

    type ContentTypeKey = keyof typeof CUSTOM_TYPE_PLURALS;
    type ResourceLabelKey = `resources.${ContentTypeKey | (typeof CUSTOM_TYPE_PLURALS)[ContentTypeKey]}`;

    const props = defineProps<{collection: CollectionDocument | FilledContentRelationshipField<'collection', string, CollectionDocumentData>}>();
    const {t} = useTranslation();
    const {getCollectionNumbers} = createContentHelpers();
    const collectionNumbers = getCollectionNumbers(props.collection);

    const CUSTOM_TYPE_PLURALS = {
        article: 'articles',
        successStories: 'successStories',
        guides: 'guides',
        events: 'events',
    } as const;

    const collectionEntries = computed(() =>
        (Object.entries(collectionNumbers) as [ContentTypeKey, number][])
            .filter(([, amount]) => amount > 0)
            .map(([contentType, amount]): {contentType: ContentTypeKey; amount: number; labelKey: ResourceLabelKey} => ({
                contentType,
                amount,
                labelKey: `resources.${amount > 1 ? CUSTOM_TYPE_PLURALS[contentType] : contentType}` as ResourceLabelKey,
            })),
    );
</script>

<style lang="scss">
    .collection-thumbnail {
        border: 1px solid var(--joy-color-primary-50);
        padding: var(--joy-core-spacing-6);
        border-radius: var(--joy-core-radius-3);
        background-image: url('../assets/svg/collection-background.svg');
        height: 220px;
        position: relative;
        overflow: hidden;
        width: 100%;
        background-repeat: no-repeat;
        background-size: cover;

        &__overlay {
            width: 100%;
            height: 100%;
            position: absolute;
            top: -3px;
            left: 0;
            background-color: var(--joy-color-neutral-70);
            z-index: 1;
            width: 220px;
            height: 72px;
            border-radius: 0 0 100px 0;
            transform: rotate(-4deg);
        }

        &__info {
            position: absolute;
            top: 15px;
            left: 25px;
            z-index: 3;
            color: var(--joy-color-neutral-0);
            transform: rotate(4deg);
        }

        &__title {
            font-size: var(--joy-font-size-primary-800);
            font-weight: var(--joy-font-weight-bold);
            font-family: var(--joy-font-family-title);
            line-height: 1;
        }

        &__label {
            color: var(--joy-color-primary-50);
            font-size: var(--joy-font-size-primary-300);
            margin-left: var(--joy-core-spacing-0);
        }
    }
</style>
